import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/apiFetch';
import API_URL from '../../config/api';

interface DashStats {
  todayAppointments: number;
  totalClients: number;
  todayRevenue: number;
  lowStockCount: number;
}

interface Appointment {
  id: number;
  client_name: string;
  service_name: string;
  start_time: string;
  status: string;
  staff_name?: string;
}

const PINK = '#ec4899';
const PINK_LIGHT = '#fdf2f8';
const PINK_MID = '#fbcfe8';

const card = (bg: string, shadow: string): React.CSSProperties => ({
  borderRadius: 16,
  padding: '22px 24px',
  background: bg,
  boxShadow: shadow,
  color: '#fff',
  cursor: 'default',
  flex: '1 1 180px',
});

function StatCard({ icon, label, value, bg, shadow, onClick }: {
  icon: string; label: string; value: string | number; bg: string; shadow: string; onClick?: () => void;
}) {
  return (
    <div style={{ ...card(bg, shadow), cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.88, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 20 }}>{icon}</span> {label}
      </div>
      <div style={{ fontSize: 38, fontWeight: 900, margin: '8px 0 2px', letterSpacing: '-1px', lineHeight: 1 }}>{value}</div>
    </div>
  );
}

export default function BeautyDashboard() {
  const { token, name, business_id } = useAuth() as any;
  const publicBookingUrl = business_id ? `${window.location.origin}/book/${business_id}` : null;
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashStats | null>(null);
  const [todayAppts, setTodayAppts] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [dashRes, apptRes] = await Promise.all([
          apiFetch(`${API_URL}/beauty/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
          apiFetch(`${API_URL}/beauty/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (dashRes.ok) setStats(await dashRes.json());
        if (apptRes.ok) {
          const all: Appointment[] = await apptRes.json();
          const today = new Date().toISOString().slice(0, 10);
          setTodayAppts(all.filter(a => {
            const d = a.start_time ? a.start_time.slice(0, 10) : '';
            return d === today;
          }).slice(0, 8));
        }
      } catch (e) {
        // errors already logged
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  const fmt = (n: number) =>
    '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const statusColour = (s: string) => {
    if (s === 'confirmed') return { bg: '#d1fae5', text: '#065f46' };
    if (s === 'completed') return { bg: '#e0e7ff', text: '#3730a3' };
    if (s === 'cancelled') return { bg: '#fee2e2', text: '#991b1b' };
    return { bg: '#f3f4f6', text: '#374151' };
  };

  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", minHeight: '100vh', background: '#f9fafb' }}>

      {/* ── Banner ── */}
      <div style={{
        background: `linear-gradient(135deg, ${PINK} 0%, #f472b6 55%, #fb7185 100%)`,
        padding: '32px 32px 28px',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
        <div style={{ position: 'absolute', bottom: -60, right: 80, width: 240, height: 240, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.85, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
            💈 Beauty Suite
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 4 }}>
            Good {getGreeting()}, {name?.split(' ')[0] || 'there'}!
          </div>
          <div style={{ fontSize: 13, opacity: 0.82 }}>{today}</div>
        </div>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: 1100 }}>

        {/* ── 4 KPI cards ── */}
        {loading ? (
          <div style={{ color: '#9ca3af', padding: '24px 0' }}>Loading…</div>
        ) : (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
            <StatCard
              icon="📅" label="Today's Appointments" value={stats?.todayAppointments ?? 0}
              bg={`linear-gradient(135deg, ${PINK} 0%, #f472b6 100%)`}
              shadow={`0 6px 20px rgba(236,72,153,0.35)`}
              onClick={() => navigate('/beauty/bookings')}
            />
            <StatCard
              icon="👤" label="Total Clients" value={stats?.totalClients ?? 0}
              bg="linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)"
              shadow="0 6px 20px rgba(124,58,237,0.3)"
              onClick={() => navigate('/customers')}
            />
            <StatCard
              icon="💷" label="Today's Revenue" value={fmt(stats?.todayRevenue ?? 0)}
              bg="linear-gradient(135deg, #059669 0%, #34d399 100%)"
              shadow="0 6px 20px rgba(5,150,105,0.3)"
              onClick={() => navigate('/orders/list')}
            />
            <StatCard
              icon="📦" label="Low Stock Alerts" value={stats?.lowStockCount ?? 0}
              bg={stats?.lowStockCount ? 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)' : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)'}
              shadow="0 6px 20px rgba(107,114,128,0.25)"
              onClick={() => navigate('/products')}
            />
          </div>
        )}

        {/* ── Today's appointments list ── */}
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '18px 24px', borderBottom: `3px solid ${PINK}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>📅 Today's Appointments</div>
            <button
              onClick={() => navigate('/beauty/bookings')}
              style={{ background: PINK_LIGHT, color: PINK, border: `1px solid ${PINK_MID}`, borderRadius: 8, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              View All
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 24, color: '#9ca3af' }}>Loading…</div>
          ) : todayAppts.length === 0 ? (
            <div style={{ padding: '32px 24px', textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
              <div style={{ fontWeight: 600 }}>No appointments today</div>
              <button
                onClick={() => navigate('/beauty/bookings')}
                style={{ marginTop: 12, background: PINK, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                + Book Appointment
              </button>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Time', 'Client', 'Service', 'Staff', 'Status'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {todayAppts.map((a, i) => {
                  const sc = statusColour(a.status);
                  return (
                    <tr key={a.id} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                        {a.start_time ? new Date(a.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#111827', fontWeight: 500 }}>{a.client_name || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{a.service_name || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{a.staff_name || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: sc.bg, color: sc.text, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
                          {a.status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Public booking link ── */}
        {publicBookingUrl && (
          <div style={{ marginTop: 24, background: '#fff', borderRadius: 14, border: `1.5px solid ${PINK_MID}`, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 3 }}>🔗 Your Public Booking Page</div>
              <div style={{ fontSize: 13, color: '#6b7280', fontFamily: 'monospace', wordBreak: 'break-all' }}>{publicBookingUrl}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => navigator.clipboard.writeText(publicBookingUrl)}
                style={{ background: PINK_LIGHT, color: PINK, border: `1.5px solid ${PINK_MID}`, borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >Copy Link</button>
              <a href={publicBookingUrl} target="_blank" rel="noopener noreferrer"
                style={{ background: PINK, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
              >Open ↗</a>
            </div>
          </div>
        )}

        {/* ── Quick actions ── */}
        <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { icon: '👤', label: 'Clients', path: '/customers' },
            { icon: '📅', label: 'New Booking', path: '/beauty/bookings' },
            { icon: '💈', label: 'Services', path: '/products' },
            { icon: '📊', label: 'Analytics', path: '/analytics' },
          ].map(q => (
            <button
              key={q.path}
              onClick={() => navigate(q.path)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: '#fff', border: `1.5px solid ${PINK_MID}`, borderRadius: 12,
                padding: '12px 20px', fontSize: 13, fontWeight: 600, color: '#374151',
                cursor: 'pointer', transition: 'all 0.15s',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = PINK_LIGHT; (e.currentTarget as HTMLElement).style.borderColor = PINK; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#fff'; (e.currentTarget as HTMLElement).style.borderColor = PINK_MID; }}
            >
              <span style={{ fontSize: 18 }}>{q.icon}</span> {q.label}
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
